// packages/web/src/components/Layout/Header.tsx
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MenuIcon from '@mui/icons-material/Menu';
import { User } from '@matchpoint/shared';

const CompactAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  height: 64,
  borderBottom: '1px solid #e5e5e5',
}));

const CompactToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: '64px !important',
  height: 64,
  maxWidth: '1200px',
  width: '100%',
  margin: '0 auto',
  padding: '0 16px !important',
  [theme.breakpoints.up('sm')]: {
    padding: '0 24px !important',
  },
}));

const LogoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
}));

const LogoIcon = styled('div')(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #84b817 0%, #c4d82f 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  boxShadow: '0 2px 6px rgba(132, 184, 23, 0.25)',
  flexShrink: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: '#c4d82f',
    top: 6,
    left: 6,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#679614',
    bottom: 6,
    right: 6,
  },
  [theme.breakpoints.down('sm')]: {
    width: 32,
    height: 32,
    '&::before': {
      width: 12,
      height: 12,
      top: 5,
      left: 5,
    },
    '&::after': {
      width: 9,
      height: 9,
      bottom: 5,
      right: 5,
    },
  },
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#1a1a1a',
  whiteSpace: 'nowrap',
  fontFamily: '"Helvetica Neue", Arial, sans-serif',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const NavSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: theme.spacing(4),
  flex: 1,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: '#666',
  fontSize: '0.95rem',
  padding: '8px 16px',
  minWidth: 'auto',
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f0f8e8',
    color: '#84b817',
  },
  '& .MuiButton-startIcon': {
    marginRight: '6px',
    '& svg': {
      fontSize: '1.1rem',
    },
  },
}));

const UserSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
}));

const CompactAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  fontSize: '0.95rem',
  cursor: 'pointer',
  backgroundColor: '#84b817',
  fontWeight: 600,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 2px 8px rgba(132, 184, 23, 0.3)',
  },
}));

const CompactIconButton = styled(IconButton)(({ theme }) => ({
  color: '#666',
  padding: 8,
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '& svg': {
    fontSize: '1.3rem',
  },
  '&:hover': {
    backgroundColor: '#f0f8e8',
    color: '#84b817',
  },
}));

const UserName = styled(Typography)(({ theme }) => ({
  color: '#1a1a1a',
  fontSize: '0.95rem',
  fontWeight: 600,
  marginRight: theme.spacing(0.5),
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  color: 'white',
  fontSize: '0.95rem',
  padding: '8px 20px',
  textTransform: 'none',
  fontWeight: 600,
  minWidth: 'auto',
  flexShrink: 0,
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#333',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
  },
  '& .MuiButton-startIcon': {
    marginRight: '6px',
    '& svg': {
      fontSize: '1rem',
    },
  },
}));

const CompactMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.95rem',
  padding: '10px 16px',
  minHeight: 'auto',
  borderRadius: '6px',
  margin: '2px 6px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f0f8e8',
    color: '#84b817',
  },
})) as typeof MenuItem;

const FlexSpacer = styled(Box)({
  flexGrow: 1,
});

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  color: '#666',
  padding: 8,
  marginLeft: theme.spacing(1),
  borderRadius: '8px',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
  },
  '& svg': {
    fontSize: '1.3rem',
  },
  '&:hover': {
    backgroundColor: '#f0f8e8',
    color: '#84b817',
  },
}));

interface HeaderProps {
  user: User | null;
  onSignOut: () => void;
  onLoginClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ user, onSignOut, onLoginClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = React.useState<null | HTMLElement>(null);
  const [guestMenuAnchorEl, setGuestMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleGuestMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setGuestMenuAnchorEl(event.currentTarget);
  };

  const handleGuestMenuClose = () => {
    setGuestMenuAnchorEl(null);
  };

  const handleSignOut = () => {
    handleMenuClose();
    onSignOut();
  };

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent any default navigation
    if (onLoginClick) {
      onLoginClick();
    }
  };

  return (
    <CompactAppBar position="sticky">
      <CompactToolbar>
        <LogoSection>
          <RouterLink to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', gap: '8px' }}>
            <LogoIcon />
            <HeaderTitle variant="h1">
              Sched.tennis
            </HeaderTitle>
          </RouterLink>
        </LogoSection>

        <NavSection>
          <RouterLink to="/calendar" style={{ textDecoration: 'none' }}>
            <NavButton startIcon={<CalendarMonthIcon />}>
              Calendar
            </NavButton>
          </RouterLink>
          
          <RouterLink to="/courts" style={{ textDecoration: 'none' }}>
            <NavButton startIcon={<SportsTennisIcon />}>
              Courts
            </NavButton>
          </RouterLink>

          <RouterLink to="/plans" style={{ textDecoration: 'none' }}>
            <NavButton startIcon={<CreditCardIcon />}>
              Pricing
            </NavButton>
          </RouterLink>
        </NavSection>

        <FlexSpacer />

        {user ? (
          <UserSection>
            <CompactIconButton
              onClick={handleNotificationMenuOpen}
              size="small"
            >
              <NotificationsIcon />
            </CompactIconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleMenuOpen}>
              <UserName>
                {user.name}
              </UserName>
              <CompactAvatar src={user.profilePicture}>
                {user.name.charAt(0).toUpperCase()}
              </CompactAvatar>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: '12px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e5e5',
                  '& .MuiList-root': {
                    py: 1,
                  },
                },
              }}
            >
              {isTablet && (
                <>
                  <CompactMenuItem component={RouterLink} to="/calendar" onClick={handleMenuClose}>
                    <CalendarMonthIcon sx={{ mr: 1.5, fontSize: '1.1rem', color: '#84b817' }} />
                    Calendar
                  </CompactMenuItem>
                  <CompactMenuItem component={RouterLink} to="/courts" onClick={handleMenuClose}>
                    <SportsTennisIcon sx={{ mr: 1.5, fontSize: '1.1rem', color: '#84b817' }} />
                    Courts
                  </CompactMenuItem>
                  <CompactMenuItem component={RouterLink} to="/plans" onClick={handleMenuClose}>
                    <CreditCardIcon sx={{ mr: 1.5, fontSize: '1.1rem', color: '#84b817' }} />
                    Pricing
                  </CompactMenuItem>
                  <Box sx={{ borderTop: '1px solid #e5e5e5', my: 1, mx: 1 }} />
                  <CompactMenuItem component={RouterLink} to="/profile" onClick={handleMenuClose}>
                    <AccountCircleIcon sx={{ mr: 1.5, fontSize: '1.1rem', color: '#84b817' }} />
                    My Profile
                  </CompactMenuItem>
                </>
              )}
              {!isTablet && (
                <CompactMenuItem component={RouterLink} to="/profile" onClick={handleMenuClose}>
                  <AccountCircleIcon sx={{ mr: 1.5, fontSize: '1.1rem', color: '#84b817' }} />
                  My Profile
                </CompactMenuItem>
              )}
              <CompactMenuItem onClick={handleSignOut} sx={{ color: '#d32f2f' }}>
                Logout
              </CompactMenuItem>
            </Menu>

            <Menu
              anchorEl={notificationAnchorEl}
              open={Boolean(notificationAnchorEl)}
              onClose={handleNotificationMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: '12px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e5e5',
                  '& .MuiList-root': {
                    py: 1,
                  },
                },
              }}
            >
              <CompactMenuItem component={RouterLink} to="/profile" onClick={handleNotificationMenuClose}>
                <Typography variant="body2" sx={{ fontSize: '0.9rem', color: '#666' }}>
                  View challenges in profile
                </Typography>
              </CompactMenuItem>
            </Menu>
          </UserSection>
        ) : (
          <>
            <LoginButton
              onClick={handleLoginClick}
              startIcon={<AccountCircleIcon />}
              size="small"
            >
              Login
            </LoginButton>
            
            {isTablet && (
              <>
                <MobileMenuButton onClick={handleGuestMenuOpen}>
                  <MenuIcon />
                </MobileMenuButton>
                
                <Menu
                  anchorEl={guestMenuAnchorEl}
                  open={Boolean(guestMenuAnchorEl)}
                  onClose={handleGuestMenuClose}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                      border: '1px solid #e5e5e5',
                      '& .MuiList-root': {
                        py: 1,
                      },
                    },
                  }}
                >
                  <CompactMenuItem component={RouterLink} to="/calendar" onClick={handleGuestMenuClose}>
                    <CalendarMonthIcon sx={{ mr: 1.5, fontSize: '1.1rem', color: '#84b817' }} />
                    Calendar
                  </CompactMenuItem>
                  <CompactMenuItem component={RouterLink} to="/courts" onClick={handleGuestMenuClose}>
                    <SportsTennisIcon sx={{ mr: 1.5, fontSize: '1.1rem', color: '#84b817' }} />
                    Courts
                  </CompactMenuItem>
                  <CompactMenuItem component={RouterLink} to="/plans" onClick={handleGuestMenuClose}>
                    <CreditCardIcon sx={{ mr: 1.5, fontSize: '1.1rem', color: '#84b817' }} />
                    Pricing
                  </CompactMenuItem>
                </Menu>
              </>
            )}
          </>
        )}
      </CompactToolbar>
    </CompactAppBar>
  );
};

export default Header;